/**
 * MillaAuth URL utilities - shared helpers for building URLs from MillaAuth config
 * @module lib/utils/millaAuth
 */

const { logWarn } = require('../logger');

/**
 * Get the MillaAuth origin URL from SDK config, with fallback
 * @param {string} [fallbackOrigin] - Fallback origin if MillaAuth not available
 * @returns {string} The origin URL (without trailing slash)
 */
function getMillaAuthOrigin(fallbackOrigin = 'https://embed.getmilla.com') {
  try {
    if (typeof window !== 'undefined') {
      const auth = window.MillaAuth;
      if (auth && typeof auth.getConfig === 'function') {
        const cfg = auth.getConfig();
        const base = (cfg && cfg.issuer) || '';
        if (base) {
          return base.replace(/\/$/, '');
        }
      }
    }
  } catch (err) {
    logWarn('Failed to resolve MillaAuth origin', err);
  }
  return fallbackOrigin.replace(/\/$/, '');
}

/**
 * Build a URL using MillaAuth origin with path and query parameters
 * @param {string} path - URL path (e.g., '/subscription', '/support')
 * @param {object} [params] - Query parameters as key-value pairs
 * @param {string} [fallbackOrigin] - Fallback origin if MillaAuth not available
 * @returns {string} The complete URL
 */
function buildMillaAuthUrl(path, params = {}, fallbackOrigin = 'https://embed.getmilla.com') {
  const origin = getMillaAuthOrigin(fallbackOrigin);
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;

  const queryParts = [];
  for (const key of Object.keys(params)) {
    const value = params[key];
    if (value !== undefined && value !== null) {
      queryParts.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`);
    }
  }

  const query = queryParts.length > 0 ? `?${queryParts.join('&')}` : '';
  return `${origin}${normalizedPath}${query}`;
}

module.exports = {
  getMillaAuthOrigin,
  buildMillaAuthUrl
};
